/*  */
import { createContext, useContext, useState, FC, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import gameService from '../../services/game';
import { DEFAULT_CITY } from '../../routes';
import { useLanguage } from '../LanguageContext';
import { ContextProps, ProviderProps } from './types';
import { LeaderboardType } from '../../types/entities';

export const LeaderboardContext = createContext<ContextProps>({
  leaderboard: [],
  fetchTop9: async () => {},
  fetchCurrentSessionLeaderboard: async () => {},
  isLoading: false,
});

export const LeaderboardProvider: FC<ProviderProps> = ({ children }: ProviderProps) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardType[]>([]);
  const [isLoading, setLoading] = useState(false);
  const { languageSelector } = useLanguage();
  const { city = DEFAULT_CITY } = useParams<{ city: string }>();

  const fetchTop9 = useCallback(async () => {
    try {
      setLoading(true);
      const res = await gameService.getTop9Leaderboard(city, languageSelector);
      setLeaderboard(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [languageSelector, city]);

  const fetchCurrentSessionLeaderboard = async (documentId: string) => {
    try {
      setLoading(true);
      const res = await gameService.getRelativeLeaderboard(documentId, languageSelector);
      setLeaderboard(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LeaderboardContext.Provider
      value={{
        leaderboard,
        fetchTop9,
        fetchCurrentSessionLeaderboard,
        isLoading,
      }}>
      {children}
    </LeaderboardContext.Provider>
  );
};

export default function useLeaderboardContext(): ContextProps {
  return useContext(LeaderboardContext);
}
